import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "@/App";
import usePageTitle from "@/hooks/usePageTitle";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { Lock, User, Flame, Eye, EyeOff } from "lucide-react";

const LoginPage = () => {
  usePageTitle("Login");
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const { login } = useAuth();

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    const maxRetries = 3;
    let lastError = null;

    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        if (attempt > 1) {
          toast.info(`Retrying... (attempt ${attempt}/${maxRetries})`);
        }

        const controller = new AbortController();
        const timeoutId = setTimeout(() => controller.abort(), 15000); // 15s timeout

        const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/auth/login`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ username, password }),
          signal: controller.signal
        });

        clearTimeout(timeoutId);
        const data = await response.json();

        if (response.ok) {
          login(data.access_token, data.user);
          toast.success("Welcome to EAC!");
          
          // Role-based redirect
          const userRole = data.user?.role;
          if (userRole === "partner") {
            navigate("/partner");
          } else if (userRole === "agent") {
            navigate("/agent");
          } else if (userRole === "uploader") {
            navigate("/upload-center");
          } else if (userRole === "error_manager") {
            navigate("/errors");
          } else {
            navigate("/dashboard");
          }
          return; // Success - exit
        } else {
          toast.error(data.detail || "Invalid credentials");
          setLoading(false);
          return; // Auth error - don't retry
        }
      } catch (error) {
        lastError = error;
        if (error.name === 'AbortError') {
          lastError = new Error('Request timed out');
        }
        // Wait before retry (1s, 2s, 3s)
        if (attempt < maxRetries) {
          await new Promise(r => setTimeout(r, attempt * 1000));
        }
      }
    }

    // All retries failed
    const msg = lastError?.message?.includes('timed out')
      ? "Server is taking too long to respond. Please wait a moment and try again."
      : "Connection error. Please check your internet or try again in a few seconds.";
    toast.error(msg);
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex">
      {/* Left Panel - Image */}
      <div className="hidden lg:flex lg:w-1/2 relative">
        <div 
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: "url('https://images.unsplash.com/photo-1559577721-ae8c3ecc3a45?crop=entropy&cs=srgb&fm=jpg&q=85')" }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-black/70 via-black/50 to-black/70" />
        <div className="relative z-10 flex flex-col justify-end p-12 text-white">
          <div className="flex items-center gap-4 mb-6">
            {/* Flame Logo */}
            <div className="relative w-16 h-16 flex items-center justify-center">
              <Flame className="w-14 h-14 text-orange-500" style={{ filter: 'drop-shadow(0 0 12px rgba(249, 115, 22, 0.6))' }} />
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-6 h-6 rounded-full bg-gradient-to-b from-orange-400 via-emerald-400 to-blue-400 opacity-50 blur-md"></div>
              </div>
            </div>
            <h1 className="font-heading text-5xl font-bold tracking-tight">
              <span className="bg-gradient-to-r from-orange-400 via-emerald-400 to-blue-400 bg-clip-text text-transparent">
                EAC
              </span>
            </h1>
          </div>
          <p className="text-xl text-white/80 max-w-md">
            Your complete solution for EXIM business management. Streamline your product and shipping price lists.
          </p>
        </div>
      </div>

      {/* Right Panel - Login Form */}
      <div className="w-full lg:w-1/2 flex items-center justify-center p-8 bg-gradient-to-br from-slate-950 via-slate-900 to-slate-950">
        <div className="w-full max-w-md">
          {/* Mobile Logo */}
          <div className="lg:hidden flex items-center gap-3 mb-8 justify-center">
            <Flame className="w-10 h-10 text-orange-500" />
            <h1 className="font-heading text-3xl font-bold bg-gradient-to-r from-orange-400 via-emerald-400 to-blue-400 bg-clip-text text-transparent">
              EAC
            </h1>
          </div>

          <div className="bg-slate-900/80 border border-slate-800 rounded-xl p-8 shadow-2xl backdrop-blur-sm">
            <div className="text-center mb-8">
              <h2 className="font-heading text-2xl font-bold text-white mb-2">Welcome Back</h2>
              <p className="text-slate-400">Sign in to your account</p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-6">
              <div className="space-y-2">
                <Label htmlFor="username" className="text-slate-300">Username</Label>
                <div className="relative">
                  <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="username"
                    data-testid="login-username"
                    type="text"
                    placeholder="Enter username"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    className="pl-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="password" className="text-slate-300">Password</Label>
                <div className="relative">
                  <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                  <Input
                    id="password"
                    data-testid="login-password"
                    type={showPassword ? "text" : "password"}
                    placeholder="Enter password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="pl-10 pr-10 bg-slate-800 border-slate-700 text-white placeholder:text-slate-500 focus:ring-orange-500 focus:border-orange-500"
                    required
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-500 hover:text-slate-300 transition-colors"
                    data-testid="toggle-password-visibility"
                  >
                    {showPassword ? (
                      <EyeOff className="w-4 h-4" />
                    ) : (
                      <Eye className="w-4 h-4" />
                    )}
                  </button>
                </div>
              </div>

              <Button
                type="submit"
                data-testid="login-submit"
                className="w-full bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-2.5 rounded-lg transition-all active:scale-[0.98] shadow-lg shadow-orange-500/25"
                disabled={loading}
              >
                {loading ? "Signing in..." : "Sign In"}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-sm text-slate-500">
                Super Admin Access Only
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default LoginPage;
